// =====================================================================
// EGL v12 Frontend - STAGING IMAGE UPLOAD COMPONENT
// Purpose: Select images from Windows server staging folder
// Pattern: COPIED FROM CardPrintingSection.tsx (PROVEN WORKING)
// =====================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react'
import './StagingImageUpload.css'

interface StagingImageUploadProps {
  location: string;
  onImageSelected: (filename: string, imageBlob: Blob) => void;
  onCancel?: () => void;
  disableCancel?: boolean;
}

interface StagingImage {
  filename: string;
  size: number;
  modified: string;
  blobUrl?: string;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
}

const IMAGES_PER_PAGE = 12
const MAX_CONCURRENT_FETCHES = 5

export const StagingImageUpload: React.FC<StagingImageUploadProps> = ({ 
  location, 
  onImageSelected, 
  onCancel,
  disableCancel = false
}) => {
  // S3 Sync state
  const [isS3Syncing, setIsS3Syncing] = useState(false)
  const [syncStats, setSyncStats] = useState({ total: 0, downloaded: 0, errors: 0 })
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null)
  const [consoleLogs, setConsoleLogs] = useState<LogEntry[]>([])
  const consoleRef = useRef<HTMLDivElement>(null)

  // Image state - allImages holds metadata only, pageImages holds current page with blobs
  const [allImages, setAllImages] = useState<StagingImage[]>([])
  const [pageImages, setPageImages] = useState<StagingImage[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [hasCompletedSync, setHasCompletedSync] = useState(false)
  
  const token = localStorage.getItem('egl_token')
  const gridRef = useRef<HTMLDivElement>(null)

  const totalPages = Math.ceil(allImages.length / IMAGES_PER_PAGE)

  // Throttled fetch: runs async tasks with a max concurrency limit
  const throttledFetchAll = useCallback(async (
    items: StagingImage[],
    fetchFn: (img: StagingImage) => Promise<StagingImage>,
    concurrency: number
  ): Promise<StagingImage[]> => {
    const results: StagingImage[] = new Array(items.length)
    let index = 0

    const worker = async () => {
      while (index < items.length) {
        const i = index++
        results[i] = await fetchFn(items[i])
      }
    }

    const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker())
    await Promise.all(workers)
    return results
  }, [])

  // Fetch blob for a single image
  const fetchImageBlob = useCallback(async (img: StagingImage): Promise<StagingImage> => {
    try {
      setLoadingImages(prev => new Set(prev).add(img.filename))
      const imgResponse = await fetch(
        `/api/card-printing/staging-images/${location}/file/${img.filename}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      )
      if (imgResponse.ok) {
        const blob = await imgResponse.blob()
        const blobUrl = URL.createObjectURL(blob)
        return { ...img, blobUrl }
      }
      console.warn(`Failed to load image: ${img.filename}`)
      return img
    } catch (err) {
      console.warn(`Error loading image ${img.filename}:`, err)
      return img
    } finally {
      setLoadingImages(prev => {
        const next = new Set(prev)
        next.delete(img.filename)
        return next
      })
    }
  }, [location, token])

  // Load blobs for current page only
  const loadPageBlobs = useCallback(async (page: number) => {
    const start = page * IMAGES_PER_PAGE
    const end = start + IMAGES_PER_PAGE
    const pageSlice = allImages.slice(start, end)

    if (pageSlice.length === 0) {
      setPageImages([])
      return
    }

    // Set page images without blobs first (shows loading placeholders)
    setPageImages(pageSlice.map(img => ({ ...img, blobUrl: undefined })))

    // Fetch blobs with throttling
    const withBlobs = await throttledFetchAll(pageSlice, fetchImageBlob, MAX_CONCURRENT_FETCHES)
    setPageImages(withBlobs)
  }, [allImages, fetchImageBlob, throttledFetchAll])

  // When page changes, revoke old blobs and load new page
  useEffect(() => {
    if (allImages.length === 0) return

    // Revoke previous page blob URLs
    pageImages.forEach(img => {
      if (img.blobUrl) URL.revokeObjectURL(img.blobUrl)
    })

    loadPageBlobs(currentPage)

    // Scroll grid to top on page change
    if (gridRef.current) {
      gridRef.current.scrollTop = 0
    }
  }, [currentPage, allImages]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup all blob URLs on unmount
  useEffect(() => {
    return () => {
      pageImages.forEach(img => {
        if (img.blobUrl) URL.revokeObjectURL(img.blobUrl)
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll sync status while syncing
  useEffect(() => {
    if (!isS3Syncing || !location) return;

    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await fetch(`/api/card-printing/sync/${location}/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          const stats = statusData.stats || { total: 0, downloaded: 0, errors: 0 };
          setSyncStats(stats);

          if (!statusData.is_syncing) {
            setIsS3Syncing(false);
            setLastSyncTime(new Date());
            setHasCompletedSync(true);
            console.log('S3 sync completed, loading images...');
            await loadImages();
          }
        }

        const logsRes = await fetch(`/api/card-printing/logs/${location}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (logsRes.ok) {
          const logsData = await logsRes.json();
          setConsoleLogs(logsData.logs || []);
          if (consoleRef.current) {
            consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
          }
        }
      } catch (err) {
        console.error('Failed to poll sync status/logs:', err);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isS3Syncing, location, token]);

  // Load image list (metadata only, no blobs)
  const loadImages = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log(`Loading staging images for location: ${location}`)
      
      const response = await fetch(
        `/api/card-printing/staging-images/${location}/list`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )
      
      if (!response.ok) {
        throw new Error(`Failed to load images: ${response.status} ${response.statusText}`)
      }
      
      const data = await response.json()
      const imageList: StagingImage[] = data.images || []
      console.log(`Loaded ${imageList.length} staging images`)
      
      setAllImages(imageList)
      setCurrentPage(0)
      // loadPageBlobs will be triggered by the useEffect watching currentPage/allImages
    } catch (error) {
      console.error('Failed to load images:', error)
      setError(error instanceof Error ? error.message : 'Failed to load staging images')
    } finally {
      setLoading(false)
    }
  }

  // Start S3 sync
  const startS3Sync = async () => {
    if (!location) {
      setError('Location not specified');
      return;
    }

    try {
      setError(null);
      setIsS3Syncing(true);
      setSyncStats({ total: 0, downloaded: 0, errors: 0 });
      setConsoleLogs([]);
      setHasCompletedSync(false);
      setAllImages([]);
      setPageImages([]);
      setCurrentPage(0);
      setSelectedImage(null);

      const response = await fetch(`/api/card-printing/sync/${location}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start S3 sync');
      }
      
      console.log('S3 sync started successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start S3 sync');
      setIsS3Syncing(false);
      console.error(err);
    }
  };

  // Cancel S3 sync
  const cancelS3Sync = async () => {
    if (!location) return;
    try {
      await fetch(`/api/card-printing/sync/${location}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIsS3Syncing(false);
      console.log('S3 sync cancelled');
    } catch (err) {
      console.error('Failed to cancel sync:', err);
    }
  };

  // Handle image selection (works across pages)
  const handleImageClick = (filename: string) => {
    setSelectedImage(selectedImage === filename ? null : filename)
  }

  // Page navigation
  const goToPage = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page)
    }
  }

  // Handle done button - re-fetches the full image for the selected filename
  const handleDone = async () => {
    if (!selectedImage) return
    
    try {
      setLoading(true)
      console.log(`Fetching selected image: ${selectedImage}`)
      
      const response = await fetch(
        `/api/card-printing/staging-images/${location}/file/${selectedImage}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      )
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`)
      }
      
      const imageBlob = await response.blob()
      console.log(`Image fetched: ${imageBlob.size} bytes`)
      onImageSelected(selectedImage, imageBlob)
      
    } catch (error) {
      console.error('Failed to fetch selected image:', error)
      setError(error instanceof Error ? error.message : 'Failed to fetch image')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="staging-image-upload">
      {/* Step 1: S3 Sync */}
      {!hasCompletedSync && (
        <div className="sync-step">
          <h4>Step 1: Sync Images from S3</h4>
          <p>Download images from S3 bucket to staging folder on Windows server.</p>
          
          {error && (
            <div className="error-message">
              <strong>Warning:</strong> {error}
            </div>
          )}
          
          <div className="sync-actions">
            <button 
              type="button"
              onClick={startS3Sync} 
              className="sync-btn primary"
              disabled={isS3Syncing}
            >
              {isS3Syncing ? 'Syncing...' : 'Start S3 Sync'}
            </button>
            
            {isS3Syncing && (
              <button 
                type="button"
                onClick={cancelS3Sync} 
                className="sync-btn secondary"
              >
                Cancel
              </button>
            )}
            
            {onCancel && !isS3Syncing && !disableCancel && (
              <button 
                type="button"
                onClick={onCancel} 
                className="sync-btn secondary"
                title="Return to upload from computer"
              >
                Back
              </button>
            )}
          </div>

          {/* Sync Stats */}
          {(isS3Syncing || lastSyncTime) && (
            <div className="sync-stats">
              <div className="stat-item">
                <span className="stat-label">Total:</span>
                <span className="stat-value">{syncStats.total}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Downloaded:</span>
                <span className="stat-value success">{syncStats.downloaded}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Errors:</span>
                <span className="stat-value error">{syncStats.errors}</span>
              </div>
              {lastSyncTime && (
                <div className="stat-item">
                  <span className="stat-label">Last Sync:</span>
                  <span className="stat-value">{lastSyncTime.toLocaleTimeString()}</span>
                </div>
              )}
            </div>
          )}

          {/* Console Logs */}
          {(isS3Syncing || consoleLogs.length > 0) && (
            <div className="console-logs">
              <div className="console-header">
                <span>S3 Sync Logs</span>
                <button 
                  type="button"
                  onClick={() => setConsoleLogs([])} 
                  className="clear-logs-btn"
                  disabled={isS3Syncing}
                >
                  Clear
                </button>
              </div>
              <div className="console-content" ref={consoleRef}>
                {consoleLogs.length === 0 ? (
                  <div className="console-empty">Waiting for logs...</div>
                ) : (
                  consoleLogs.map((log, idx) => (
                    <div key={idx} className={`console-line ${log.level.toLowerCase()}`}>
                      <span className="console-timestamp">{log.timestamp}</span>
                      <span className="console-level">[{log.level}]</span>
                      <span className="console-message">{log.message}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Select Image */}
      {hasCompletedSync && (
        <div className="image-selection-step">
          <h4>Step 2: Select Image ({allImages.length} available)</h4>
          
          {error && (
            <div className="error-message">
              <strong>Warning:</strong> {error}
            </div>
          )}
          
          {loading && allImages.length === 0 ? (
            <div className="loading-images">
              <div className="spinner"></div>
              <p>Loading images...</p>
            </div>
          ) : allImages.length === 0 ? (
            <div className="no-images">
              <p>No images found in staging folder</p>
              <button 
                type="button"
                onClick={() => {
                  setHasCompletedSync(false);
                  setLastSyncTime(null);
                }} 
                className="sync-btn secondary"
              >
                Sync Again
              </button>
            </div>
          ) : (
            <>
              {/* Pagination controls - top */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="sync-btn secondary pagination-btn"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 0}
                  >
                    Prev
                  </button>
                  <span className="pagination-info">
                    Page {currentPage + 1} of {totalPages} ({allImages.length} images)
                  </span>
                  <button
                    type="button"
                    className="sync-btn secondary pagination-btn"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                  </button>
                </div>
              )}

              <div className="staging-grid" ref={gridRef}>
                {pageImages.map(img => (
                  <div 
                    key={img.filename}
                    className={`grid-item ${selectedImage === img.filename ? 'selected' : ''}`}
                    onClick={() => handleImageClick(img.filename)}
                  >
                    <div className="grid-item-image-container">
                      {img.blobUrl ? (
                        <img 
                          src={img.blobUrl} 
                          alt={img.filename}
                        />
                      ) : (
                        <div className="image-loading">
                          {loadingImages.has(img.filename) ? (
                            <div className="image-spinner"></div>
                          ) : (
                            'Loading...'
                          )}
                        </div>
                      )}
                    </div>
                    <div className="image-info">
                      <div className="filename">{img.filename}</div>
                      <div className="filesize">{Math.round(img.size / 1024)}KB</div>
                    </div>
                    {selectedImage === img.filename && (
                      <div className="selected-badge">✓</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination controls - bottom */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="sync-btn secondary pagination-btn"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 0}
                  >
                    Prev
                  </button>
                  <span className="pagination-info">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="sync-btn secondary pagination-btn"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                  </button>
                </div>
              )}
              
              <div className="selection-actions">
                <button 
                  type="button"
                  onClick={() => {
                    setHasCompletedSync(false);
                    setLastSyncTime(null);
                  }} 
                  className="sync-btn secondary"
                >
                  Sync Again
                </button>
                
                {selectedImage && (
                  <div className="done-section">
                    <p className="selected-info">
                      <strong>Selected:</strong> {selectedImage}
                    </p>
                    <button 
                      type="button"
                      onClick={handleDone} 
                      className="sync-btn primary done-btn"
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Done'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
